import { LoadingOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Select, Spin, InputNumber, Col, Row } from "antd";
import React, { useState } from "react";
import CreatePlanAttributeApi from "../../../api/attributes/CreatePlanAttributeApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";

const { Option } = Select;

function AddPlanAttributeModal(props) {
  const [submit, setSubmit] = useState(false);
  const [selectedValueType, setSelectedValueType] = useState(null);
  const [form] = Form.useForm();

  function handleCancel() {
    setSubmit(false);
    setSelectedValueType(null);
    form.resetFields();
    props.handleCancel();
  }

  function handleValueTypeChange(value) {
    setSelectedValueType(value);
    // Reset double-specific fields when changing away from double
    if (value !== 'double') {
      form.setFieldsValue({
        decimalPrecision: undefined,
        minValue: undefined,
        maxValue: undefined,
        unit: undefined,
      });
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    
    // For now, just send basic fields to work with existing backend
    // TODO: Enhance backend to handle double attribute configuration
    const apiResponse = await CreatePlanAttributeApi(values.name, values.valueType);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess(
        "bottomRight",
        "Plan attribute added successfully"
      );
      props.handleOk();
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't add plan attribute. Try again later."
      );
    }
    handleCancel();
  }

  return (
    <Modal
      title="Add plan attribute"
      visible={props.visible}
      onCancel={() => handleCancel()}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        name="planGroup"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          label="Attribute name*"
          name="name"
          rules={[
            { required: true, message: "Please enter a Attribute name!" },
          ]}
        >
          <Input placeholder="Name" />
        </Form.Item>

        <Form.Item
          label="Value type*"
          name="valueType"
          rules={[{ required: true, message: "Please select a Value type!" }]}
        >
          <Select
            allowClear
            showSearch
            placeholder="Select type"
            onChange={handleValueTypeChange}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) =>
              optionA.children
                .toLowerCase()
                .localeCompare(optionB.children.toLowerCase())
            }
          >
            <Option key={"text"} value={"text"}>
              Text
            </Option>
            <Option key={"number"} value={"number"}>
              Number
            </Option>
            <Option key={"double"} value={"double"}>
              Double (Decimal)
            </Option>
            <Option key={"date"} value={"date"}>
              Date
            </Option>
            <Option key={"dropdown"} value={"dropdown"}>
              Dropdown
            </Option>
          </Select>
        </Form.Item>

        {/* Double attribute configuration fields */}
        {selectedValueType === 'double' && (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Decimal Precision"
                  name="decimalPrecision"
                  tooltip="Number of decimal places (0-10)"
                  initialValue={2}
                >
                  <InputNumber
                    min={0}
                    max={10}
                    style={{ width: "100%" }}
                    placeholder="2"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Unit"
                  name="unit"
                  tooltip="Optional unit (e.g., USD, %, kg)"
                >
                  <Input placeholder="e.g., USD, %, kg" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Minimum Value"
                  name="minValue"
                  tooltip="Optional minimum allowed value"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="No minimum"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Maximum Value"
                  name="maxValue"
                  tooltip="Optional maximum allowed value"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="No maximum"
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* Dropdown attribute info */}
        {selectedValueType === 'dropdown' && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded mb-4">
            <div className="text-sm text-blue-700">
              <strong>Note:</strong> After creating this dropdown attribute, you can manage its values 
              (options) in the "Dropdown Attributes" section of the settings.
            </div>
          </div>
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <div className=" flex justify-end">
            <Button className="mr-3 w-28" onClick={() => handleCancel()}>
              Cancel
            </Button>
            <Button
              className="btn w-32"
              type="primary"
              htmlType={submit && "submit"}
              disabled={submit}
            >
              {submit ? (
                <Spin
                  size="small"
                  indicator={<LoadingOutlined style={{ color: "#fff" }} spin />}
                />
              ) : (
                "Add"
              )}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default AddPlanAttributeModal;
